import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useUserHasSeen } from '@openclassrooms/utils/local-storage/hooks';
import { translate } from '@openclassrooms/translations';
import { styled, alpha } from '@classify/react-core-components/styles';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import Stack from '@classify/react-core-components/Stack';
import Typography from '@classify/react-core-components/Typography';
import Button from '@classify/react-core-components/Button';
import IconButton from '@classify/react-core-components/IconButton';
import { SHOULD_DISPLAY_MAINTENANCE_BANNER } from '../../hooks/useMaintenanceStaticContent';
import { SlotTopBannerContent } from '../..';
interface Props {
  userId: string | number;
  shouldSeeBanner?: boolean;
  content: SlotTopBannerContent;
}
const CloseButton = styled(IconButton)(({
  theme: {
    palette,
    spacing
  }
}) => ({
  padding: spacing(0.5),
  margin: spacing(0.5),
  '.MuiSvgIcon-root': {
    color: palette.text.onMain
  },
  '&:hover:not(:active), &:focus': {
    backgroundColor: alpha(palette.interaction.onContrasted.hover, 0.5)
  }
}));
const BannerWrapper = styled('section')(({
  theme: {
    palette,
    spacing,
    zIndex,
    breakpoints
  }
}) => ({
  display: 'none',
  padding: spacing(2, 0, 1, 0),
  backgroundColor: SHOULD_DISPLAY_MAINTENANCE_BANNER ? palette.notification.warning.background : palette.brand.accent.option1,
  position: 'relative',
  zIndex: zIndex.appBar,
  [breakpoints.up('sm')]: {
    display: 'block',
    padding: spacing(1, 0)
  }
}));
const BannerButton = styled(Button)(({
  theme: {
    palette
  }
}) => ({
  '&&, &&:hover, &&:focus': {
    color: palette.text.onMain
  },
  '&:hover:not(:active), &:focus': {
    backgroundColor: SHOULD_DISPLAY_MAINTENANCE_BANNER ? palette.interaction.onSelected : palette.interaction.onContrasted.hover
  }
}));
const TopBanner = t0 => {
  const $ = _c(29);
  const {
    content,
    userId,
    shouldSeeBanner
  } = t0;
  const {
    storageKey
  } = content;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      shouldPreventAutoSeen: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    hasSeen: hasSeenFeature,
    setSeen
  } = useUserHasSeen(storageKey, userId, t1);
  const [isOpen, setIsOpen] = useState(false);
  let t2;
  let t3;
  if ($[1] !== hasSeenFeature || $[2] !== shouldSeeBanner) {
    t2 = () => {
      if (hasSeenFeature === false && shouldSeeBanner !== false) {
        setIsOpen(true);
      }
      if (shouldSeeBanner === false || hasSeenFeature === true) {
        setIsOpen(false);
      }
    };
    t3 = [hasSeenFeature, shouldSeeBanner];
    $[1] = hasSeenFeature;
    $[2] = shouldSeeBanner;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== setSeen) {
    t4 = () => {
      setSeen();
      setIsOpen(false);
    };
    $[5] = setSeen;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleClose = t4;
  if (storageKey && !isOpen || !content?.body) {
    return null;
  }
  const {
    type,
    category,
    action,
    label: dataEventLabel
  } = content.dataEvent;
  const dataEventContent = type ? `${type}|${category}|${action}|${dataEventLabel}` : undefined;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = translate("navigation.word.annonce");
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      sm: 1
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      sm: "row"
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      sm: 1
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      py: 1
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== content.body) {
    t10 = <Typography variant="subheading" sx={t9} component="p">{content.body}</Typography>;
    $[12] = content.body;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== content.button || $[15] !== content.isTargetBlankLink || $[16] !== content.link || $[17] !== dataEventContent) {
    t11 = content?.button && <BannerButton variant="tertiary" href={content.link} data-event={dataEventContent} {...content.isTargetBlankLink && {
      target: "_blank"
    }}>{content.button}</BannerButton>;
    $[14] = content.button;
    $[15] = content.isTargetBlankLink;
    $[16] = content.link;
    $[17] = dataEventContent;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <Stack flexDirection={t7} flexWrap="wrap" gap={t8}>{t10}{t11}</Stack>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = translate("global.action.close_button");
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <CloseIcon />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== handleClose) {
    t15 = <CloseButton aria-label={t13} size="large" onClick={handleClose}>{t14}</CloseButton>;
    $[24] = handleClose;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t12 || $[27] !== t15) {
    t16 = <BannerWrapper aria-label={t5}><ContentWrapper><Stack flexDirection="row" flexWrap="nowrap" gap={t6} alignItems="flex-start" justifyContent="space-between">{t12}{t15}</Stack></ContentWrapper></BannerWrapper>;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
};
export default TopBanner;